import { type ZodTypeAny, type infer as ZodInfer, ZodError } from "zod";

import { store } from "./store";

/**
 * Given a Zod parser and an API response, attempt to run the parser on the
 * response. If the result of parsing throws an error, catches the error and
 * prints it to the console.
 *
 * Generic is `S extends ZodTypeAny` (not `ZodSchema<T>`) so the return type is
 * `z.infer<S>` — the schema's *output* type. With `ZodSchema<T>`, TS has to
 * fit a single T into both input and output, and for schemas where they
 * differ (e.g. `.optional().transform()`, `.default()`) it picks the input
 * shape, which silently gives consumers the pre-transform type. This broke
 * consumers of `ticketParticipantsSchema` after PR #3365 (`name` became
 * `string | undefined` on the consumer side even though the schema fills it
 * in at parse time).
 */
export function parseResponse<S extends ZodTypeAny>(
  parser: S,
  obj: unknown,
): ZodInfer<S> | undefined {
  try {
    const parsedData = parser.parse(obj);
    return parsedData;
  } catch (e) {
    if (e instanceof ZodError) {
      // Check if we're in the middle of logging out - if so, don't log errors
      const { auth } = store.getState();
      if (auth.loggingOut) {
        throw e;
      }

      console.groupCollapsed("ZodError");
      const formatted = e.format();

      Object.keys(formatted).map((key) => {
        if (key === "_errors" && Array.isArray(key) && !key.length) {
          return;
        }

        console.groupCollapsed(key);

        // @ts-expect-error TS7053: we aren't using this type in a meaningful
        // way
        console.error(JSON.stringify(formatted[key], null, 4));

        console.groupEnd();
      });

      console.groupEnd();

      throw e;
    }
  }
}
